import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';
import { GrammarError } from '../extensions/GrammarCheck';

interface GrammarSuggestionPopupProps {
  editor: Editor;
  error: GrammarError;
  onApplySuggestion: (errorId: string, replacement: string) => void;
  onDismiss: (errorId: string) => void;
  onClose: () => void;
}

const GrammarSuggestionPopup: React.FC<GrammarSuggestionPopupProps> = ({
  editor, error, onApplySuggestion, onDismiss, onClose
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Position the popup directly below the error word — runs synchronously before paint
  useLayoutEffect(() => {
    if (!editor || !error) return;

    try {
      const view = editor.view;
      const coords = view.coordsAtPos(error.from);
      const container = view.dom.closest('.notion-editor-content') as HTMLElement | null;

      if (coords && container) {
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const scrollLeft = container.scrollLeft;

        // Position below the error word, accounting for scroll
        let top = coords.bottom - containerRect.top + scrollTop + 6;
        let left = coords.left - containerRect.left + scrollLeft;

        // Ensure popup doesn't overflow right edge
        const popupWidth = 300;
        if (left + popupWidth > container.clientWidth) {
          left = Math.max(0, container.clientWidth - popupWidth - 8);
        }

        // Ensure left is not negative
        left = Math.max(4, left);

        setPosition({ top, left });
      }
    } catch {
      setPosition({ top: 100, left: 16 });
    }
  }, [editor, error]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!position) return null;

  return (
    <div
      ref={popupRef}
      className="grammar-suggestion-popup"
      style={{ top: position.top, left: position.left }}
    >
      <div className="grammar-popup-header">
        <span className={`grammar-category-badge grammar-${error.rule.issueType}`}>
          {error.rule.category || error.rule.issueType}
        </span>
        <button className="grammar-popup-close" onClick={onClose} title="Close">
          &times;
        </button>
      </div>

      <p className="grammar-popup-message">{error.message}</p>

      {error.replacements.length > 0 && (
        <div className="grammar-popup-suggestions">
          <span className="grammar-popup-label">Suggestions:</span>
          <div className="grammar-suggestion-list">
            {error.replacements.map((replacement, index) => (
              <button
                key={index}
                className="grammar-suggestion-btn"
                onClick={() => onApplySuggestion(error.id, replacement)}
                title={`Replace with "${replacement}"`}
              >
                {replacement}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grammar-popup-actions">
        <button className="grammar-dismiss-btn" onClick={() => onDismiss(error.id)}>
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default GrammarSuggestionPopup;
